import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Avatar } from "@mui/material";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { ProfilePictureService, ApiService, MyService } from "../../../api";
import { toInitials, toInitialsUserName, getAvatarColor } from "../../utils";
import { setUserProfilePicture } from "../../slice/contextSlice";
import { isFeatureEnabled, Flags, isSegFeatureEnabled, SegFlags } from "../../providers";
import styles from "./UserAvatar.module.css";

const profilePictureCache = new Map();
const pendingRequests = new Map();
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

const CURRENT_USER_CACHE_KEY = "current-user-profile-picture";

const getCacheEntry = (key) => {
	const entry = profilePictureCache.get(key);
	if (!entry) {
		return null;
	}
	if (Date.now() - entry.timestamp > CACHE_EXPIRATION_MS) {
		profilePictureCache.delete(key);
		return null;
	}
	return entry.url;
};

const setCacheEntry = (key, url) => {
	profilePictureCache.set(key, { url, timestamp: Date.now() });
};

const deleteCacheEntry = (key) => {
	profilePictureCache.delete(key);
};

const getCacheKey = (userId, companyId, projectId) => {
	if (projectId) {
		return `project-${projectId}-user-${userId}`;
	}
	if (companyId) {
		return `company-${companyId}-user-${userId}`;
	}
	return `user-${userId}`;
};

const validateBlobUrl = async (url) => {
	if (!url || typeof url !== "string") {
		return false;
	}
	if (!url.startsWith("blob:")) {
		return true;
	}

	try {
		const response = await fetch(url, { method: "HEAD" });
		return response.ok;
	} catch {
		return false;
	}
};

const fetchCurrentUserPicture = async (cancelToken) => {
	try {
		const response = await MyService.getProfilePicture(cancelToken);
		if (response?.data) {
			return URL.createObjectURL(response.data);
		}
		return null;
	} catch {
		return null;
	}
};

const fetchOtherUserPicture = async (userId, companyId, projectId, cancelToken) => {
	try {
		const service = projectId
			? ProfilePictureService.getByProject({ projectId, userId }, cancelToken)
			: ProfilePictureService.getByCompany({ companyId, userId }, cancelToken);

		const response = await service;
		if (response?.data) {
			return URL.createObjectURL(response.data);
		}
		return null;
	} catch {
		return null;
	}
};

const UserAvatar = ({
	userId,
	companyId,
	projectId,
	displayName,
	firstName,
	lastName,
	email,
	size = "md",
	className = "",
	src: directSrc,
	isCurrentUser = false,
	customSize = null,
}) => {
	const [imageUrl, setImageUrl] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [fetchKey, setFetchKey] = useState(0);
	const mountedRef = useRef(true);
	const cancelTokenRef = useRef(null);
	const dispatch = useDispatch();
	const currentUser = useSelector(({ context }) => context.user);
	const reduxProfilePicture = useSelector((state) => state.context.user?.profilePictureUrl);
	const hasLoadedRef = useRef(false);
	const isThisCurrentUser = useMemo(() => {
		if (isCurrentUser) {
			return true;
		}
		if (currentUser?.id && userId && currentUser.id === userId) {
			return true;
		}
		return false;
	}, [isCurrentUser, currentUser?.id, userId]);

	const computedDisplayName = useMemo(() => {
		if (displayName) {
			return displayName;
		}
		if (firstName && lastName) {
			return `${firstName} ${lastName}`;
		}
		if (firstName) {
			return firstName;
		}
		if (lastName) {
			return lastName;
		}
		if (email) {
			return email.split("@")[0];
		}
		return "";
	}, [displayName, firstName, lastName, email]);
	const initials = useMemo(() => {
		if (firstName || lastName) {
			return toInitials(firstName, lastName);
		}
		if (displayName) {
			return toInitialsUserName(displayName);
		}
		if (email) {
			return email.charAt(0).toUpperCase();
		}
		return "";
	}, [firstName, lastName, displayName, email]);
	const avatarColor = useMemo(
		() => getAvatarColor(computedDisplayName || email || `user-${userId}`),
		[computedDisplayName, email, userId]
	);
	const cacheKey = useMemo(() => getCacheKey(userId, companyId, projectId), [userId, companyId, projectId]);

	useEffect(() => {
		if (!isThisCurrentUser) {
			return undefined;
		}
		if (!isFeatureEnabled(Flags.PROFILE_PICTURE)) {
			return undefined;
		}
		if (directSrc) {
			setImageUrl(directSrc);
			return undefined;
		}
		if (hasLoadedRef.current && fetchKey === 0) {
			return undefined;
		}
		mountedRef.current = true;
		let isCancelled = false;
		const loadPicture = async () => {
			const currentReduxPicture = reduxProfilePicture;
			if (currentReduxPicture) {
				if (!isCancelled) {
					setImageUrl(currentReduxPicture);
					hasLoadedRef.current = true;
				}
				return;
			}
			const cachedUrl = getCacheEntry(CURRENT_USER_CACHE_KEY);
			if (cachedUrl) {
				const isValid = await validateBlobUrl(cachedUrl);
				if (isValid && !isCancelled) {
					setImageUrl(cachedUrl);
					dispatch(setUserProfilePicture(cachedUrl));
					hasLoadedRef.current = true;
					return;
				}
				deleteCacheEntry(CURRENT_USER_CACHE_KEY);
			}
			if (pendingRequests.has(CURRENT_USER_CACHE_KEY)) {
				try {
					const url = await pendingRequests.get(CURRENT_USER_CACHE_KEY);
					if (url && !isCancelled) {
						setImageUrl(url);
						hasLoadedRef.current = true;
					}
				} catch (error) {
					console.error(error);
				}
				return;
			}
			setIsLoading(true);
			const cancelTokenSource = ApiService.getCancelTokenSource();
			cancelTokenRef.current = cancelTokenSource;
			const fetchPromise = fetchCurrentUserPicture(cancelTokenSource.token);
			pendingRequests.set(CURRENT_USER_CACHE_KEY, fetchPromise);
			try {
				const url = await fetchPromise;
				if (!isCancelled && mountedRef.current) {
					if (url) {
						setCacheEntry(CURRENT_USER_CACHE_KEY, url);
						setImageUrl(url);
						dispatch(setUserProfilePicture(url));
						hasLoadedRef.current = true;
					}
					setIsLoading(false);
				}
			} catch {
				if (!isCancelled && mountedRef.current) {
					setIsLoading(false);
				}
			} finally {
				pendingRequests.delete(CURRENT_USER_CACHE_KEY);
			}
		};
		loadPicture();
		return () => {
			isCancelled = true;
			mountedRef.current = false;
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isThisCurrentUser, directSrc, dispatch, fetchKey]);
	useEffect(() => {
		if (!isThisCurrentUser) {
			return;
		}
		if (directSrc) {
			return;
		}

		if (reduxProfilePicture && reduxProfilePicture !== imageUrl) {
			setImageUrl(reduxProfilePicture);
		} else if (!reduxProfilePicture && imageUrl) {
			setImageUrl(null);
		}
	}, [isThisCurrentUser, directSrc, reduxProfilePicture, imageUrl]);
	useEffect(() => {
		if (isThisCurrentUser) {
			return undefined;
		}
		if (!isFeatureEnabled(Flags.PROFILE_PICTURE)) {
			return undefined;
		}
		if (projectId && !isSegFeatureEnabled(SegFlags.PROFILE_PICTURE)) {
			return undefined;
		}
		if (directSrc) {
			setImageUrl(directSrc);
			return undefined;
		}
		if (!userId || (!companyId && !projectId)) {
			return undefined;
		}
		mountedRef.current = true;
		let isCancelled = false;
		const loadPicture = async () => {
			const cachedUrl = getCacheEntry(cacheKey);
			if (cachedUrl !== null) {
				if (cachedUrl) {
					const isValid = await validateBlobUrl(cachedUrl);
					if (isValid && !isCancelled) {
						setImageUrl(cachedUrl);
						return;
					}
					deleteCacheEntry(cacheKey);
				} else {
					return;
				}
			}
			if (pendingRequests.has(cacheKey)) {
				try {
					const url = await pendingRequests.get(cacheKey);
					if (!isCancelled && url) {
						setImageUrl(url);
					}
				} catch (error) {
					console.error(error);
				}
				return;
			}
			setIsLoading(true);
			const cancelTokenSource = ApiService.getCancelTokenSource();
			cancelTokenRef.current = cancelTokenSource;
			const fetchPromise = fetchOtherUserPicture(userId, companyId, projectId, cancelTokenSource.token);
			pendingRequests.set(cacheKey, fetchPromise);
			try {
				const url = await fetchPromise;
				if (!isCancelled && mountedRef.current) {
					if (url) {
						setCacheEntry(cacheKey, url);
						setImageUrl(url);
					} else {
						setCacheEntry(cacheKey, null);
					}
					setIsLoading(false);
				}
			} catch {
				if (!isCancelled && mountedRef.current) {
					setIsLoading(false);
				}
			} finally {
				pendingRequests.delete(cacheKey);
			}
		};
		loadPicture();
		return () => {
			isCancelled = true;
			mountedRef.current = false;
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
	}, [isThisCurrentUser, userId, companyId, projectId, cacheKey, directSrc, fetchKey]);
	const handleImageError = useCallback(() => {
		setImageUrl(null);
		if (isThisCurrentUser) {
			deleteCacheEntry(CURRENT_USER_CACHE_KEY);
			dispatch(setUserProfilePicture(null));
			hasLoadedRef.current = false;
		} else {
			deleteCacheEntry(cacheKey);
		}
		setFetchKey((prev) => prev + 1);
	}, [isThisCurrentUser, cacheKey, dispatch]);

	const sizeClass = customSize ? "" : styles[`avatar--${size}`] || styles["avatar--md"];
	const customSizeStyle = useMemo(() => {
		if (!customSize) {
			return {};
		}
		const sizeValue = typeof customSize === "number" ? `${customSize}px` : customSize;
		return {
			width: sizeValue,
			height: sizeValue,
			fontSize: typeof customSize === "number" ? `${Math.max(customSize * 0.4, 10)}px` : undefined,
		};
	}, [customSize]);
	const hasValidImage = imageUrl && !isLoading;
	return (
		<Avatar
			alt={computedDisplayName}
			className={`${styles.avatar} ${sizeClass} ${className}`}
			imgProps={{ onError: handleImageError }}
			src={hasValidImage ? imageUrl : undefined}
			sx={{ ...(hasValidImage ? {} : avatarColor), ...customSizeStyle }}
		>
			{initials}
		</Avatar>
	);
};

UserAvatar.propTypes = {
	userId: PropTypes.number,
	companyId: PropTypes.number,
	projectId: PropTypes.number,
	displayName: PropTypes.string,
	firstName: PropTypes.string,
	lastName: PropTypes.string,
	email: PropTypes.string,
	size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "profile"]),
	className: PropTypes.string,
	src: PropTypes.string,
	isCurrentUser: PropTypes.bool,
	customSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

UserAvatar.defaultProps = {
	userId: null,
	companyId: null,
	projectId: null,
	displayName: "",
	firstName: "",
	lastName: "",
	email: "",
	size: "md",
	className: "",
	src: null,
	isCurrentUser: false,
	customSize: null,
};

export const clearProfilePictureCache = (userId, companyId, projectId) => {
	if (userId && (companyId || projectId)) {
		const key = getCacheKey(userId, companyId, projectId);
		profilePictureCache.delete(key);
	} else {
		profilePictureCache.clear();
	}
	profilePictureCache.delete(CURRENT_USER_CACHE_KEY);
};

export const updateProfilePictureCache = (userId, companyId, projectId, imageUrl) => {
	const key = getCacheKey(userId, companyId, projectId);
	setCacheEntry(key, imageUrl);
	setCacheEntry(CURRENT_USER_CACHE_KEY, imageUrl);
};

export const preloadProfilePicture = async (userId, companyId, projectId, cancelToken) => {
	const key = getCacheKey(userId, companyId, projectId);

	const cached = getCacheEntry(key);
	if (cached !== null) {
		return cached;
	}

	if (pendingRequests.has(key)) {
		return pendingRequests.get(key);
	}

	if (!cancelToken) {
		return null;
	}

	const fetchPromise = fetchOtherUserPicture(userId, companyId, projectId, cancelToken);
	pendingRequests.set(key, fetchPromise);

	try {
		const url = await fetchPromise;
		if (url) {
			setCacheEntry(key, url);
		} else {
			setCacheEntry(key, null);
		}
		return url;
	} finally {
		pendingRequests.delete(key);
	}
};

export const resetCurrentUserProfilePictureFetch = () => {
	deleteCacheEntry(CURRENT_USER_CACHE_KEY);
};

export default UserAvatar;
